import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, TextField, DialogActions, Button, MenuItem, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './styles.module.css';
import { AlertMessagesContext } from 'react-alert-messages';
import ChargeServices from 'services/ChargeServices';

export default function ChargeEditDialog({ chargeGroups, charge, handleClose, handleSuccess }) {
  const { t: tCommon } = useTranslation('common');
  const [data, setData] = useState({
    ...charge,
    group_id: charge.group?.id,
    calculation_type: charge.calculation_type || charge.type,
    category: charge.category || 'CHARGE'
  });
  const [isLoading, setLoading] = useState(false);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const handleUpdateCharge = async () => {
    setLoading(true);
    try {
      const resp = await ChargeServices.updateCharge(data.id, data);
      postAlertMessage({ type: 'success', text: tCommon('Charge updated successfully') });
      handleSuccess(resp);
      handleClose();
    } catch (err) {
      postAlertMessage({ type: 'error', text: err.message || tCommon('Failed to update charge.') });
    }
    setLoading(false);
  };

  const isFormValid = Object.entries(data).every(([key, value]) => {
    if (['order', 'min_amount', 'max_amount'].includes(key)) {
      return value === '' || value === null || typeof value === 'number';
    }
    return value !== '' && value !== null;
  });

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setData((prev) => ({
      ...prev,
      [field]: ['order', 'value', 'min_amount', 'max_amount'].includes(field)
        ? value === ''
          ? ''
          : Number(value)
        : value,
    }));
  };

  const chargeFields = [
    {
      label: tCommon('Group'),
      field: 'group_id',
      type: 'select',
      options: chargeGroups.reduce((acc, group) => ({ ...acc, [group.id]: group.name }), {}),
    },
    { label: tCommon('Name'), field: 'name', type: 'text' },
    {
      label: tCommon('Category'),
      field: 'category',
      type: 'select',
      options: { CHARGE: tCommon('Charge'), DISCOUNT: tCommon('Discount') },
    },
    { label: tCommon('Order'), field: 'order', type: 'number' },
    { label: tCommon('Value'), field: 'value', type: 'number' },
    {
      label: tCommon('Calculation Type'),
      field: 'calculation_type',
      type: 'select',
      options: { AMOUNT: tCommon('Amount'), PERCENT: tCommon('Percent') },
    },
    {
      label: tCommon('Apply On'),
      field: 'apply_on',
      type: 'select',
      options: { BASE: tCommon('Base'), PREV: tCommon('Prev') },
    },
    { label: tCommon('Min Amount'), field: 'min_amount', type: 'number' },
    { label: tCommon('Max Amount'), field: 'max_amount', type: 'number' },
  ];

  return (
    <>
      <div className={styles.popUp}>
        <div className={styles.contentWrapper}>
          <div className={styles.headSec}>
            <h2 className={styles.editTitle}>{tCommon('Edit Charge')}</h2>
            <IconButton onClick={handleClose} className={styles.closeBtn}><CloseIcon/></IconButton>
          </div>

          <div className={styles.inputSec}>
            {chargeFields.map(({ label, field, type, options = [] }) => (
              <Grid container className={styles.inputRow} alignItems="center" key={field}>
                <Grid item xs={4} sm={4} className={styles.inputLabelContainer}>
                  <h3 className={styles.inputLabel}>{label}</h3>
                </Grid>
                <Grid item xs={8} sm={8}>
                  {type === 'select' ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={data[field]}
                      onChange={handleChange(field)}
                      displayEmpty
                    >
                      {Object.keys(options).map((option) => (
                        <MenuItem key={option} value={option}>
                          {options[option]}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      label={label}
                      variant="outlined"
                      size="small"
                      fullWidth
                      type={type}
                      value={data[field]}
                      onChange={handleChange(field)}
                      inputProps={type === 'number' ? { step: 'any' } : {}}
                    />
                  )}
                </Grid>
              </Grid>
            ))}

            <DialogActions>
              <Button variant="contained" color="secondary" size="small" onClick={handleClose} disabled={isLoading}>
                {tCommon('Close')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ backgroundColor: '#00a65a' }}
                onClick={handleUpdateCharge}
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                    {tCommon('Updating...')}
                  </>
                ) : (
                  tCommon('Update')
                )}
              </Button>
            </DialogActions>
          </div>
        </div>
      </div>
    </>
  );
}
